import { computed, ref, shallowRef, watch } from 'vue';
import { useIntervalFn } from '@vueuse/core';
import { scheduleStatus, TScheduleStatus } from '@/domain/catalog/availability';
import { useRestaurantStore } from '@/stores/restaurant.store';
import type { ICatalogData } from './use-catalog-data';

/** How often the «Fuera de horario» states are recomputed. */
const TICK_MS = 60_000;

type TStatuses = Record<number, TScheduleStatus | null>;

function sameStatus(
  a: TScheduleStatus | null | undefined,
  b: TScheduleStatus | null
): boolean {
  return JSON.stringify(a ?? null) === JSON.stringify(b);
}

/**
 * The new statuses, keeping the previous object of every unchanged one;
 * null when nothing changed at all (the cards then keep their render).
 */
function mergeStatuses(previous: TStatuses, next: TStatuses): TStatuses | null {
  const merged: TStatuses = {};
  let changed = Object.keys(previous).length !== Object.keys(next).length;
  for (const key of Object.keys(next)) {
    const id = Number(key);
    if (id in previous && sameStatus(previous[id], next[id]))
      merged[id] = previous[id];
    else {
      merged[id] = next[id];
      changed = true;
    }
  }
  return changed ? merged : null;
}

/**
 * The time-dependent statuses of the Menú page (plan §5.6): only products
 * and categories with a schedule are recomputed on the one-minute tick, and
 * the maps change only when a status does (an opening or closing time), so
 * the list never re-renders on the clock alone.
 */
export function useCatalogSchedule(data: ICatalogData) {
  const restaurantStore = useRestaurantStore();
  const now = ref(new Date());
  useIntervalFn(() => {
    now.value = new Date();
  }, TICK_MS);

  const context = computed(() => ({
    tier: restaurantStore.effectiveTier,
    tz: restaurantStore.selectedBranch?.location?.tz ?? null,
    now: now.value,
  }));

  const itemStatuses = shallowRef<TStatuses>({});
  const categoryStatuses = shallowRef<TStatuses>({});

  watch(
    [data.rows, context],
    () => {
      const next: TStatuses = {};
      for (const { item, category } of data.rows.value)
        if (item.hourRanges?.length || category?.hourRanges?.length)
          next[item.id] = scheduleStatus(
            [item.hourRanges, category?.hourRanges],
            context.value
          );
      const merged = mergeStatuses(itemStatuses.value, next);
      if (merged) itemStatuses.value = merged;
    },
    { immediate: true }
  );

  watch(
    [data.sections, context],
    () => {
      const next: TStatuses = {};
      for (const { category } of data.sections.value)
        if (category.hourRanges?.length)
          next[category.id] = scheduleStatus(
            [category.hourRanges],
            context.value
          );
      const merged = mergeStatuses(categoryStatuses.value, next);
      if (merged) categoryStatuses.value = merged;
    },
    { immediate: true }
  );

  return { itemStatuses, categoryStatuses };
}

export type ICatalogSchedule = ReturnType<typeof useCatalogSchedule>;
